import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useParams, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import DailyIframe from "@daily-co/daily-js";
import { get_video_join_info_api, get_group_video_join_info_api } from "../../../DAL/SchoolPortal/Tutors";
import { font, accent, success } from "../../../layouts/schoolPortal/schoolPortalTheme";
import { markInSession, clearInSession, PRESENCE_HEARTBEAT_MS } from "../../../utils/sessionRoom";

/**
 * The lesson itself, at its own address: /school/call/:bookingId.
 *
 * WHY A PAGE AND NOT THE MODAL IT REPLACES
 * Teaching a lesson is not an interruption to the Teaching page — it IS the
 * work, and a 900px box floating over a calendar you can still see is a video
 * call pretending to be a notification. Three concrete costs: a stray click on
 * the backdrop ended a lesson mid-sentence; there was no URL, so a crash or a
 * reload meant starting over and the call could not be put on a second screen;
 * and an instructor who needed to open a student's record during a lesson had
 * to choose between the record and the call.
 *
 * Now it opens in its own tab, fills it, and the address is the session, so a
 * refresh rejoins rather than restarts and closing the tab is the deliberate
 * act leaving a lesson should be. Mirrors the student side exactly
 * (web-frontend/pages/session/[bookingId].jsx).
 *
 * DELIBERATELY OUTSIDE SchoolPortalLayout. A sidebar beside a lesson is a set
 * of ways to accidentally leave it, and the layout's SessionJoinPrompt would
 * otherwise sit in the corner of the call inviting the instructor to join the
 * call they are already in.
 *
 * A GROUP CLASS IS THE SAME PAGE with ?group=1 — both endpoints answer with the
 * same envelope, so only which one gets called differs.
 */
const prettySubject = (v) =>
  String(v).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function SessionRoom() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isGroup = params.get("group") === "1";

  const containerRef = useRef(null);
  const callFrameRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [waiting, setWaiting] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [recording, setRecording] = useState(null);
  const [left, setLeft] = useState(false);
  const [details, setDetails] = useState(null);   // subject + who, for the end screen
  const [closingIn, setClosingIn] = useState(null);

  // Closing the tab is the honest way out, but a tab this page did not open
  // cannot close itself, so leaving falls back to the Teaching page.
  const leave = useCallback(() => {
    setLeft(true);
    window.close();
  }, []);

  useEffect(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    let timer = null;
    setLoading(true);
    setError("");
    setWaiting(null);
    setRecording(null);

    const run = async () => {
      const res = isGroup
        ? await get_group_video_join_info_api(bookingId)
        : await get_video_join_info_api(bookingId);
      if (cancelled) return;

      if (res.code !== 200) {
        setError(res.message || "Couldn't load this session's video room");
        setLoading(false);
        return;
      }

      if (res.data?.canJoin === false) {
        setWaiting(res.data);
        setLoading(false);
        if (res.data.state === "too_early") {
          const secs = Math.max(1, res.data.secondsUntilOpen || 1);
          setCountdown(secs);
          // Re-ask when it should have opened rather than polling: the server
          // already said exactly when, and the wait can be twenty minutes.
          timer = setTimeout(run, (secs + 1) * 1000);
        }
        return;
      }

      if (!res.data?.url) {
        setError("Couldn't load this session's video room");
        setLoading(false);
        return;
      }

      setWaiting(null);
      setRecording(res.data.recording || null);
      setDetails({ subject: res.data.subject || null, withName: res.data.withName || null });

      // join({url, token}) takes the token as its own property (Daily's
      // DailyCallOptions reference), never appended to the room URL.
      const callFrame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: true,
        iframeStyle: { width: "100%", height: "100%", border: "0" },
      });
      callFrameRef.current = callFrame;
      callFrame.on("left-meeting", () => {
        clearInSession();
        // Destroy immediately. Without this, Daily's own "you have left the
        // call" screen stays mounted underneath ours, so the tab shows two end
        // screens at once and the one on top is not the one people read.
        if (callFrameRef.current) {
          callFrameRef.current.destroy();
          callFrameRef.current = null;
        }
        setLeft(true);
      });
      // On "joined-meeting", not on mount: sitting in Daily's pre-lobby sorting
      // your camera out is not being in the lesson, and the prompt in the
      // portal tab should still be there if you back out of it.
      callFrame.on("joined-meeting", () => markInSession(bookingId));

      /**
       * Hand over to Daily the moment its iframe exists, NOT when join()
       * resolves.
       *
       * Daily shows a pre-lobby ("Are you ready to join?" with camera and mic
       * pickers), and join() does not resolve until the person clicks its Join
       * button. Clearing `loading` on that promise therefore left our own
       * "Joining the lesson…" overlay covering the very button that would have
       * resolved it — a deadlock that looked like a frozen call, except the
       * camera preview underneath was visibly live.
       *
       * From here Daily owns the waiting state, and it is better at it than a
       * spinner of ours.
       */
      if (!cancelled) setLoading(false);

      try {
        await callFrame.join({ url: res.data.url, token: res.data.token });
      } catch (joinError) {
        if (!cancelled) {
          setError(joinError.message || "Couldn't join the call");
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (callFrameRef.current) {
        callFrameRef.current.destroy();
        callFrameRef.current = null;
      }
    };
  }, [bookingId, isGroup]);

  /**
   * Keep the cross-tab presence fresh while the call is up, and clear it on the
   * way out however that happens: leaving, closing the tab, or a crash (which
   * runs no cleanup at all, hence the heartbeat the reader ages out).
   */
  useEffect(() => {
    if (left || !bookingId) return undefined;
    const beat = setInterval(() => { if (callFrameRef.current) markInSession(bookingId); }, PRESENCE_HEARTBEAT_MS);
    const bye = () => clearInSession();
    window.addEventListener("beforeunload", bye);
    return () => {
      clearInterval(beat);
      window.removeEventListener("beforeunload", bye);
      clearInSession();
    };
  }, [bookingId, left]);

  /**
   * Close the tab by itself after the call.
   *
   * This tab exists for one lesson and that lesson is over, so leaving it open
   * is leaving litter — but closing instantly would snatch the end screen away
   * before it has been read, and window.close() is refused for tabs the script
   * did not open. Hence a visible fifteen seconds and a button that works
   * either way.
   */
  useEffect(() => {
    if (!left) return undefined;
    setClosingIn(15);
    const t = setInterval(() => {
      setClosingIn((n) => {
        if (n === null) return null;
        if (n <= 1) {
          clearInterval(t);
          window.close();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [left]);

  useEffect(() => {
    if (!countdown) return undefined;
    const t = setInterval(() => setCountdown((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  if (!localStorage.getItem("token")) return <Navigate to="/login" replace />;

  const clock = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const sec = secs % 60;
    return h ? `${h}h ${m}m` : m ? `${m}m ${String(sec).padStart(2, "0")}s` : `${sec}s`;
  };

  const centred = {
    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 1, p: 3, textAlign: "center",
    // Belt and braces: nothing of ours drawn over Daily's iframe may swallow a
    // click meant for it. The buttons inside re-enable it.
    pointerEvents: "none",
    "& button": { pointerEvents: "auto" },
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, backgroundColor: "#0d0d0f", overflow: "hidden" }}>
      {left ? (
        <Box sx={centred}>
          <Typography sx={{ fontFamily: font, fontSize: "20px", fontWeight: 800, color: "#fff" }}>
            {[details?.subject && prettySubject(details.subject), details?.withName && `with ${details.withName}`]
              .filter(Boolean).join(" ") || "Your session"} has ended
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#aaa", maxWidth: 380 }}>
            The recording appears in the session once it has processed.
          </Typography>
          {closingIn !== null && (
            <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: "#6b6660", mt: 0.5 }}>
              This tab will close automatically in {closingIn}s
            </Typography>
          )}
          <Button
            onClick={() => navigate("/school/teaching")}
            sx={{
              mt: 1, backgroundColor: accent, color: "#fff", textTransform: "none",
              fontFamily: font, fontWeight: 700, fontSize: "14px", borderRadius: "999px",
              px: 3, py: 1.1, "&:hover": { backgroundColor: accent, opacity: 0.9 },
            }}
          >
            Back to teaching
          </Button>
        </Box>
      ) : (
        <>
          {loading && !error && !waiting && (
            <Box sx={{ ...centred, color: "#fff" }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "50%",
                border: "3px solid rgba(255,255,255,0.2)", borderTopColor: accent,
                animation: "sessionRoomSpin 0.8s linear infinite",
                "@keyframes sessionRoomSpin": { to: { transform: "rotate(360deg)" } },
              }} />
              <Typography sx={{ fontFamily: font, fontSize: "13px" }}>Joining the lesson…</Typography>
            </Box>
          )}

          {waiting && (
            <Box sx={centred}>
              {waiting.state === "ended" ? (
                <>
                  <Typography sx={{ fontFamily: font, fontSize: "16px", fontWeight: 700, color: "#fff" }}>
                    This session has finished
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#aaa" }}>
                    The recording appears in the session once it has processed.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ fontFamily: font, fontSize: "16px", fontWeight: 700, color: "#fff" }}>
                    The room opens in {clock(countdown)}
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#aaa" }}>
                    You will be let in automatically. You can leave this tab open.
                  </Typography>
                </>
              )}
              <Button
                onClick={leave}
                sx={{
                  mt: 1, color: "#aaa", textTransform: "none", fontFamily: font,
                  fontSize: "13px", borderRadius: "999px", px: 2.5,
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                Leave
              </Button>
            </Box>
          )}

          {error && (
            <Box sx={centred}>
              <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 700, color: "#f87171" }}>
                {error}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#aaa" }}>
                Try refreshing this tab in a moment.
              </Typography>
            </Box>
          )}

          {/* Sessions record as a matter of policy now, so this should never
              appear — it stays because an instructor who expected a recording
              has to be told there will not be one, at the time, rather than
              discovering it when they go looking for the video. */}
          {recording && !recording.willRecord && !loading && (
            <Box sx={{
              position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
              zIndex: 2, backgroundColor: "rgba(0,0,0,0.6)", borderRadius: "999px", px: 2, py: 0.75,
            }}>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#fbbf24" }}>
                Not being recorded
              </Typography>
            </Box>
          )}

          {recording && recording.willRecord && !loading && (
            <Box sx={{
              position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
              zIndex: 2, backgroundColor: "rgba(0,0,0,0.6)", borderRadius: "999px", px: 2, py: 0.75,
            }}>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: success }}>
                Recording
              </Typography>
            </Box>
          )}

          <Box ref={containerRef} sx={{ width: "100%", height: "100%" }} />
        </>
      )}
    </Box>
  );
}
